import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { SKILLMASTER_API } from "./config";
import "../styles/Payment.css";

// Book being paid for, kept so PaymentSuccess / PaymentFailed can show it after PayGlocal redirects back
export const PENDING_PAYMENT_KEY = "pendingPayment";

const Checkout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { book } = location.state || {};

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handlePayment = async () => {
    const email = localStorage.getItem("userEmail");
    if (!email) {
      navigate("/login");
      return;
    }
    if (!book?.id || !book?.source) {
      setError("Book details are incomplete. Please open the book again from the library.");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${SKILLMASTER_API}/payment/initiate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, book_source: book.source, book_id: String(book.id) }),
      });
      const data = await res.json();

      if (data.success && data.redirect_url) {
        try {
          localStorage.setItem(
            PENDING_PAYMENT_KEY,
            JSON.stringify({ txn: data.merchant_txn_id, amount: data.amount, book })
          );
        } catch (e) {
          /* storage unavailable: success page still reads the status from the server */
        }
        // PayGlocal requires a GET redirect to its payment page
        window.location.assign(data.redirect_url);
        return;
      }
      setError(data.message || "Payment could not be started. Please try again.");
    } catch (err) {
      setError("Network error. Please check your connection and try again.");
    }
    setLoading(false);
  };

  if (!book) {
    return (
      <div className="checkout-container">
        <div className="error-state">
          <div className="error-icon">📚</div>
          <h2>No Book Selected</h2>
          <p>Please go back and select a book to purchase.</p>
          <button className="btn-secondary" onClick={() => navigate(-1)}>
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-container">
      <div className="checkout-wrapper">
        {/* Header */}
        <div className="checkout-header">
          <button className="back-btn4" onClick={() => navigate(-1)} aria-label="Go back">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M19 12H5M12 19L5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <h1>Secure Checkout</h1>
          <div className="security-badge">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="shield-icon">
              <path
                d="M12 2L4 6V12C4 17 8 21 12 22C16 21 20 17 20 12V6L12 2Z"
                fill="#A5D6A7"
                stroke="#4CAF50"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"/>
            </svg>
            Secured
          </div>
        </div>

        <div className="checkout-content">
          {/* Order Summary */}
          <div className="order-summary">
            <h3>Order Summary</h3>
            <div className="book-item">
              <div className="book-icon">📖</div>
              <div className="book-details">
                <h4>{book.pdf_name}</h4>
              </div>
            </div>
            <div className="total-section">
              <div className="total-row">
                <span>Subtotal</span>
                <span>₹{book.price}</span>
              </div>
              <div className="total-row">
                <span>Tax</span>
                <span>₹0</span>
              </div>
              <div className="total-row total">
                <span>Total</span>
                <span>₹{book.price}</span>
              </div>
            </div>
          </div>

          {/* Payment */}
          <div className="payment-form">
            <h3>Payment</h3>
            <p style={{ color: "#475569", lineHeight: 1.6, marginBottom: "1rem" }}>
              You will be taken to PayGlocal's secure payment page to pay with card, UPI or net banking.
              Your card details are entered only on PayGlocal, never on SkillMaster.
            </p>

            <div className="payment-methods">
              <p>We accept:</p>
              <div className="method-icons">
                <div className="method-icon visa">VISA</div>
                <div className="method-icon mastercard">MC</div>
                <div className="method-icon rupay">RuPay</div>
                <div className="method-icon">UPI</div>
              </div>
            </div>

            {error && (
              <p className="error-text" style={{ display: "block", margin: "0.75rem 0" }}>
                {error}
              </p>
            )}

            <p style={{ color: "#64748b", fontSize: "0.85rem", lineHeight: 1.5, margin: "0.75rem 0" }}>
              By paying you agree to our{" "}
              <Link to="/refund_policy">Refund Policy</Link> and{" "}
              <Link to="/cancellation_policy">Cancellation Policy</Link>.
            </p>

            <button
              className={`pay-btn ${loading ? "loading" : ""}`}
              onClick={handlePayment}
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className="spinner-small"></div>
                  Redirecting to PayGlocal...
                </>
              ) : (
                <>Pay ₹{book.price} securely</>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
